

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Field, isComputedField } from "../types/Field";
import { DataType } from "../types/enums/DataType";
import {
  updateField as updateFieldApi,
  deleteField as deleteFieldApi,
  createField as createFieldApi,
} from "@/lib/api/handlers/AdditionalFieldHandling";
import {
  throwServerError,
  type ServerError,
} from "@/lib/api/errors/ErrorHandler";
import { boundsInWrongOrder } from "@/lib/additionalFieldConstraints";
import { useCapabilitiesOptional } from "./CapabilitiesProvider";
import { optimisticListMutate } from "@/lib/optimistic";
import { useActivityFilterOptions } from "@/hooks/useActivityFilterOptions";
import { useEvent } from "@/lib/query/hooks/useEvent";
import { GUEST_FAMILY_FIELD_NAMES } from "@/lib/utils/default-returner";
import { allowlistLookup, isFieldAllowed } from "@/lib/authz/fieldAllowlist";
import { smsChannelEnabledOf, withoutSmsFieldsUnless } from "@/lib/sms/smsCapabilities";
import { invalidateRoleTemplateViews } from "@/lib/authz/invalidateRoleTemplateViews";

type FieldsContextType = {
  allFieldsState: Field[];
  allFields: Field[];
  /**
   * The full, allowlist-UNFILTERED field set. Use this (not `allFields`) when
   * you need to know whether a field still EXISTS — e.g. pruning deleted
   * columns from a view. Keying on the filtered `allFields` would wrongly treat
   * a CUSTOM-role allowlist-hidden field as deleted.
   */
  allFieldsUnfiltered: Field[];
  /**
   * `aiContentTool` (J6, Art. 50 KI-VO): the AI lane behind the create — the
   * JSON transfer passes its `ctx.aiContentTool`. Event path only; the
   * company-CRM `api` backend has no AI mark and ignores it.
   */
  createField: (field: Field, aiContentTool?: string) => Promise<void>;
  updateField: (fieldId: string, field: Partial<Field>) => Promise<void>;
  deleteField: (fieldId: string) => Promise<void>;
  setAllFieldsState: React.Dispatch<React.SetStateAction<Field[]>>;
  /**
   * True when this provider persists through a custom `api` backend (the
   * company-CRM contact-field page) instead of the event-scoped endpoints.
   * Company fields support a narrower DataType set — the backend rejects
   * IMAGE with 400 `unsupportedCompanyFieldOption` — so the field-create
   * dialog reads this to hide unsupported datatypes. Event surfaces always
   * see `false`.
   */
  hasCustomApi: boolean;
};

const FieldsContext = createContext<FieldsContextType | undefined>(undefined);


/**
 * Built-in guest field names that can carry event-level overlays (custom enum
 * colors via {@code event.fieldDisplayColors} and conditional-display rules via
 * {@code event.fieldConditions}). Built-in fields have no field document, so
 * these overlays are merged onto them reactively from the event (see
 * `overlaidFields` below) — derived from the constants so the set never drifts.
 */
const OVERLAYABLE_BUILTIN_NAMES = GUEST_FAMILY_FIELD_NAMES;

/**
 * Which catalogue this provider holds — derived from the field's own
 * `appliesTo` so the two can never drift apart. Decides whether the CUSTOM
 * role's `fieldAllowlist` may govern the list (see the `domain` prop).
 */
export type FieldDomain = NonNullable<Field["appliesTo"]>;

export const FieldsProvider = ({
  subscription = false,
  allFields,
  appendix,
  children,
  eventId,
  allowlist,
  domain = "GUEST",
  api,
  onFieldsMutated,
}: {
  subscription?: boolean;
  appendix?: Field[];
  allFields: Field[];
  children: React.ReactNode;
  eventId?: string;
  /**
   * Which catalogue `allFields` is. Default "GUEST" — every historical mount
   * holds guest columns.
   *
   * It exists for ONE decision: whether the enclosing CapabilitiesProvider's
   * `fieldAllowlist` may be inherited. That list is a list of GUEST columns —
   * the role template documents it as "top-level fixed columns +
   * additionalFields.* keys" (`EventRoleTemplate.java`) and every enforcement
   * point is guest-domain (GuestFieldMasker, GuestQueryGateway, the guest
   * CSV/Excel exports, ActivityService, GuestLogService, CheckInLogService,
   * CohortKpiAccess); `AdditionalFieldResource` never applies it to a
   * catalogue at all. A SPEAKER/SPONSOR definition therefore has no key on any
   * guest allowlist, so inheriting one there deleted EVERY speaker/sponsor
   * custom field from the settings sheet of a field-restricted role — a
   * catalogue that allowlist has no authority over. Naming the domain beats
   * the old implicit rule (`allowlist={null}` on some mounts, absence on
   * others), where forgetting a prop silently re-enabled the wrong filter.
   */
  domain?: FieldDomain;
  /**
   * Alternative persistence backend for field CRUD. When provided, the three
   * network calls route through it INSTEAD of the event-scoped
   * AdditionalFieldHandling API — everything else (optimistic state, local
   * validation, error shaping) stays identical. Used by the company CRM page
   * to persist company-level contact fields; when omitted the provider
   * behaves exactly as before (event path).
   */
  api?: {
    createField: (field: Field) => Promise<string | ServerError>;
    updateField: (fieldId: string, field: Partial<Field>) => Promise<void | ServerError>;
    deleteField: (fieldId: string) => Promise<void | ServerError>;
  };
  /**
   * Fired after every SUCCESSFUL create/update/delete (alongside
   * `invalidateFieldQueries`, which self-disables without an eventId). Lets a
   * non-event host (CRM page) invalidate its own field-list query key.
   */
  onFieldsMutated?: () => void;
  /**
   * CUSTOM-role field allowlist from the user's template. When non-null,
   * `allFieldsState` is filtered to fields whose `additionalFields.<name>`
   * key (or fixed field name) is in the allowlist.
   *
   * When the prop is omitted, falls back to the enclosing
   * {@link CapabilitiesProvider}'s `fieldAllowlist` so every event-scoped
   * FieldsProvider mounted under EventLayoutShell automatically respects the
   * caller's CUSTOM-template restriction without each consumer threading it.
   * Pages outside the event shell (builders, settings) get the degraded
   * fallback ({@code null}) and remain unrestricted.
   *
   * `null` = no restriction.
   */
  allowlist?: string[] | null;
}) => {
  const caps = useCapabilitiesOptional();
  // Explicit prop wins; otherwise inherit from CapabilitiesContext — but only
  // on the GUEST domain, the only catalogue a `fieldAllowlist` addresses (see
  // the prop). Pages outside the event shell get `null` (no restriction) from
  // the optional hook's fallback, preserving pre-P4 behavior in builders.
  const effectiveAllowlist =
    allowlist !== undefined ? allowlist : domain === "GUEST" ? caps.fieldAllowlist : null;
  const allowSet = useMemo(
    () => allowlistLookup(effectiveAllowlist),
    [effectiveAllowlist],
  );
  // `isFieldAllowed` is the shared rule (structural identifiers and action
  // columns are exempt; a custom field is addressed as
  // `additionalFields.<name>`, a built-in by its bare name) — shared so the
  // field-definition EXPORT cannot drift from what this provider shows.
  const filterByAllowlist = useCallback(
    (fields: Field[]): Field[] => {
      if (!allowSet) return fields;
      return fields.filter((f) => isFieldAllowed(f, allowSet));
    },
    [allowSet],
  );
  // State holds the FULL, unfiltered merged list. The CUSTOM-role allowlist is
  // applied as a DERIVED view (`visibleFields`) rather than baked into state.
  // This is deliberate: the allowlist resolves asynchronously (roleQ fires
  // after the first mount), and an effect that re-seeded state from the prop on
  // every allowlist change used to clobber any field the user had just
  // created/updated/deleted — making CRUD vanish until a manual page refresh.
  // Deriving instead means allowlist resolution never touches CRUD state.
  const [allFieldsState, setAllFieldsState] = useState<Field[]>(() => [
    ...allFields,
    ...(appendix ?? []),
  ]);

  // Tier-2 activity filters (offering/room/template) carry an `optionSource`
  // instead of static options. Fetch the per-event sources (gated: only when
  // such a field is actually in the set, so builder/settings pages never fetch)
  // and merge the resolved id→name options into those fields. Additive — every
  // other field passes through untouched.
  const hasOptionSourceFields = useMemo(
    () => allFieldsState.some((f) => f.optionSource),
    [allFieldsState],
  );
  const dynamicOptions = useActivityFilterOptions(eventId, hasOptionSourceFields);
  const enrichedFields = useMemo(() => {
    if (!hasOptionSourceFields) return allFieldsState;
    return allFieldsState.map((f) => {
      if (!f.optionSource) return f;
      const dyn = dynamicOptions[f.name];
      if (!dyn) return f;
      return {
        ...f,
        options: dyn.options,
        optionLabels: dyn.optionLabels,
        optionsPending: dyn.pending,
      };
    });
  }, [allFieldsState, hasOptionSourceFields, dynamicOptions]);

  // Built-in field overlays (colors + conditional-display rules) live on the
  // EVENT doc, not on a field document. Subscribe to them via the cached event
  // query and merge them onto the built-in fields as a DERIVED layer, so an
  // admin edit (which invalidates ["events","detail",eventId]) propagates LIVE
  // to every consumer — without re-seeding `allFieldsState` (init-once) or
  // relying on a fragile optimistic patch. Gated to surfaces that actually hold
  // built-in guest fields and a real eventId, so builder/settings/create pages
  // never fetch. Mirrors the `enrichedFields` (optionSource) derived pattern.
  const hasBuiltInGuestFields = useMemo(
    () =>
      allFieldsState.some(
        (f) => !f.additionalField && OVERLAYABLE_BUILTIN_NAMES.has(f.name),
      ),
    [allFieldsState],
  );
  const isRealEventId = !!eventId && eventId !== "0" && eventId !== "root";
  const overlayEnabled = hasBuiltInGuestFields && isRealEventId;
  const eventOverlayQ = useEvent(
    overlayEnabled ? eventId! : null,
    "fieldDisplayColors,fieldConditions,fieldRequired",
  );
  const fieldDisplayColors = eventOverlayQ.data?.fieldDisplayColors;
  const fieldConditions = eventOverlayQ.data?.fieldConditions;
  const fieldRequired = eventOverlayQ.data?.fieldRequired;

  const overlaidFields = useMemo(() => {
    if (
      !overlayEnabled ||
      (!fieldDisplayColors && !fieldConditions && !fieldRequired)
    ) {
      return enrichedFields;
    }
    return enrichedFields.map((f) => {
      if (f.additionalField || !OVERLAYABLE_BUILTIN_NAMES.has(f.name)) return f;
      const cond = fieldConditions?.[f.name];
      // SET from the authoritative event value, CLEAR (undefined/false) when
      // absent — idempotent with the seed-time merge and makes a removal live.
      // `required` falls back to the constant's intrinsic value (e.g. `state`)
      // when the event has no per-field override.
      return {
        ...f,
        optionColors: fieldDisplayColors?.[f.name],
        visibilityCondition: cond?.visibilityCondition,
        editableCondition: cond?.editableCondition,
        optionConditions: cond?.optionConditions,
        required: fieldRequired?.[f.name] ?? f.required,
      };
    });
  }, [
    overlayEnabled,
    enrichedFields,
    fieldDisplayColors,
    fieldConditions,
    fieldRequired,
  ]);

  // SMS channel wave: the SMS consent built-ins (`smsOptIn` & co.) sit in the
  // static catalogues but exist for the organiser only while the backend switch
  // is on (`smsChannelEnabled`, fail closed — a page outside the event shell
  // reads the fallback and never shows them). Hidden here, at the one place
  // every column picker, filter, import mapper and the detail form read from,
  // so no surface offers a field whose writes the backend would strip.
  const smsChannelEnabled = smsChannelEnabledOf(caps);
  const visibleFields = useMemo(
    () => withoutSmsFieldsUnless(filterByAllowlist(overlaidFields), smsChannelEnabled),
    [filterByAllowlist, overlaidFields, smsChannelEnabled],
  );

  // After a field CRUD succeeds, mark the OTHER surfaces' field-list caches
  // stale so they re-seed fresh on their next mount (builders, dashboard field
  // lists, the standalone guest-detail popup) — without touching this provider's
  // own state. This is deliberately narrow: only field-list query keys, never
  // the `["eventDashboard", eventId]` prefix (that would refetch widget/analytics
  // data and flash skeletons). Already-mounted instances keep their state (they
  // seed once); the next remount picks up the fresh data.
  const queryClient = useQueryClient();
  const invalidateFieldQueries = useCallback((changed?: Pick<Field, "appliesTo">) => {
    if (!eventId) return;
    // Builders + role-templates: ["additionalFields", eventId, "guest"].
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        q.queryKey[0] === "additionalFields" &&
        q.queryKey[1] === eventId,
    });
    // Dashboard field lists ONLY (not the expensive sibling sub-keys).
    queryClient.invalidateQueries({ queryKey: ["eventDashboard", eventId, "guestFields"] });
    queryClient.invalidateQueries({ queryKey: ["eventDashboard", eventId, "eventFields"] });
    // Standalone guest-detail popup seed.
    queryClient.invalidateQueries({ queryKey: ["guestDetailSeed", eventId] });
    // Init bundles that carry the field list into seed-once providers
    // (guestlist / requests / check-in history / sent mails). Without these, a
    // remount within staleTime re-seeds FieldsProvider from the pre-CRUD
    // snapshot: a created field's column vanishes, a deleted one resurrects —
    // and GuestsProvider's stale-field prune can then silently drop pending
    // inline edits typed into the new column. refetchType "none": mounted
    // instances keep their live state; the next mount refetches.
    for (const key of [
      ["guestlist", eventId, "init"],
      ["guestsRequests", eventId],
      ["checkInHistory", eventId, "init"],
      ["sentMails", eventId, "init"],
      // Wave A hub pages seed their field list from these init bundles —
      // without the bust, a sub-staleTime remount resurrects a deleted
      // SPEAKER/SPONSOR column (or drops a created one).
      ["speakersHub", eventId, "init"],
      ["sponsorsHub", eventId, "init"],
      // Page-scoped field-list copies.
      ["checkInDevices", eventId, "fields"],
      ["eventSettings", eventId, "rules", "fields"],
      ["eventAutomationWorkflows", eventId, "fields"],
      ["msgMail", eventId, "fields"],
      ["eventAttachments", eventId, "data"],
    ]) {
      queryClient.invalidateQueries({ queryKey: key, refetchType: "none" });
    }
    // The root dashboard mounts this provider with eventId="root"; its field
    // list lives under the root namespace.
    if (eventId === "root") {
      queryClient.invalidateQueries({ queryKey: ["root", "fields"], refetchType: "none" });
    }
    // B2: a GUEST field create/update/delete runs the backend's revoke
    // cascades on this event's role templates (key pulled, `version` bumped),
    // so every cached template copy is stale — a stale version is a 409 on the
    // next save. The guest field catalogue sits under ["additionalFields",
    // eventId] and was marked by the predicate above. Not for a custom `api`
    // (company CRM fields), not for "root", not for a SPEAKER/SPONSOR field
    // (the cascades are GUEST-only); the field's own `appliesTo` wins over the
    // mount's `domain`, absent = GUEST like the backend reads it.
    if (!api && (changed?.appliesTo ?? domain) === "GUEST") {
      void invalidateRoleTemplateViews(queryClient, eventId, { catalogue: false });
    }
  }, [queryClient, eventId, api, domain]);

  const createField = useCallback(
    async (field: Field, aiContentTool?: string): Promise<void> => {
      if (subscription) return;
      if (!field.name) throw new Error("noFieldName");
      if (!field.dataType) throw new Error("noFieldDatatype");
      if (!field.additionalField) throw new Error("validationError");

      const existsField = allFieldsState.filter(
        (allField) => allField.name === field.name,
      );

      if (existsField?.length > 0) throw new Error("fieldAlreadyExists");

      if (field.lowerBound && field.upperBound) {
        if (boundsInWrongOrder(field.lowerBound, field.upperBound))
          throw new Error("lowerBoundBiggerThanUpperBound");
      }

      if (
        field.dataType === DataType.SINGLE_SELECT ||
        field.dataType === DataType.MULTI_SELECT
      )
        if ((field.options?.length ?? 0) <= 0)
          throw new Error("noFieldOptionsSpecified");

      // Optimistic: show the new field immediately, reconcile its real id on
      // success, roll the list back if the create fails (the caller's catch
      // still surfaces the violations/error).
      // `editable` is derived, never trusted from the payload. The catalogue
      // loader (`ViewFilterHandling`) mints `editable: !formula` for every
      // field it reads; this entry never passes through it, so without the
      // same derivation a brand-new computed column was offered as writable
      // until the next refetch — and an edit typed into it is overwritten by
      // the derive, which reads as the app losing the edit.
      const optimisticField: Field = {
        ...field,
        editable: !isComputedField(field),
        fieldId: crypto.randomUUID(),
      };
      await optimisticListMutate(
        setAllFieldsState,
        (prev) => [...prev, optimisticField],
        async () => {
          const response = api
            ? await api.createField(field)
            : aiContentTool
              ? await createFieldApi(eventId!, field, aiContentTool)
              : await createFieldApi(eventId!, field);
          if (response && typeof response === "object" && "isServerError" in response) {
            // Carries violations/violationsTruncated AND the raw backend
            // detail on the thrown error.
            throwServerError(response);
          }
          field.fieldId = response as string;
        },
        (prev) =>
          prev.map((f) =>
            f === optimisticField
              ? { ...optimisticField, fieldId: field.fieldId! }
              : f,
          ),
      );
      invalidateFieldQueries(field);
      onFieldsMutated?.();
    },
    [allFieldsState, eventId, invalidateFieldQueries, api, onFieldsMutated],
  );

  // Add these methods to your FieldsProvider
  const updateField = useCallback(
    async (fieldId: string, field: Partial<Field>): Promise<void> => {
      if (subscription) return;
      if (!fieldId) throw new Error("noFieldId");

      // Find the existing field
      const existingField = allFieldsState.find((f) => f.fieldId === fieldId);
      if (!existingField) throw new Error("fieldNotFound");

      // Validate bounds if both are provided
      if (field.lowerBound !== undefined && field.upperBound !== undefined) {
        if (boundsInWrongOrder(field.lowerBound, field.upperBound))
          throw new Error("lowerBoundBiggerThanUpperBound");
      }

      // Validate options for select types
      if (
        field.dataType === DataType.SINGLE_SELECT ||
        field.dataType === DataType.MULTI_SELECT ||
        existingField.dataType === DataType.SINGLE_SELECT ||
        existingField.dataType === DataType.MULTI_SELECT
      ) {
        const finalOptions = field.options ?? existingField.options;
        if ((finalOptions?.length ?? 0) <= 0)
          throw new Error("noFieldOptionsSpecified");
      }

      // Optimistic: patch locally first, roll back if the update fails.
      await optimisticListMutate(
        setAllFieldsState,
        (prev) => prev.map((f) => (f.fieldId === fieldId ? { ...f, ...field } : f)),
        async () => {
          const response = api
            ? await api.updateField(fieldId, field)
            : await updateFieldApi(eventId!, fieldId, field);
          if (
            response &&
            "isServerError" in response &&
            response.isServerError === true
          ) {
            // Carries violations/violationsTruncated AND the raw backend
            // detail on the thrown error.
            throwServerError(response);
          }
        },
      );
      invalidateFieldQueries(existingField);
      onFieldsMutated?.();
    },
    [allFieldsState, eventId, invalidateFieldQueries, api, onFieldsMutated],
  );

  const deleteField = useCallback(
    async (fieldId: string): Promise<void> => {
      if (subscription) return;
      if (!fieldId) throw new Error("noFieldId");

      // Check if field exists
      const existingField = allFieldsState.find((f) => f.fieldId === fieldId);
      if (!existingField) throw new Error("fieldNotFound");

      // Optimistic: remove locally first, restore if the delete fails.
      try {
        await optimisticListMutate(
          setAllFieldsState,
          (prev) => prev.filter((f) => f.fieldId !== fieldId),
          async () => {
            const response = api
              ? await api.deleteField(fieldId)
              : await deleteFieldApi(eventId!, fieldId);
            if (
              response &&
              "isServerError" in response &&
              response.isServerError === true
            )
              throwServerError(response);
          },
        );
      } catch (e) {
        // B2 F2 (audit r2 C-LOW-5): the field still exists, but the backend's
        // revoke already pulled it from role lists (and bumped their
        // versions) — the role template copies are stale all the same.
        if (
          eventId &&
          !api &&
          e instanceof Error &&
          e.message === "fieldDeleteFailedRolesRevoked" &&
          (existingField.appliesTo ?? domain) === "GUEST"
        ) {
          void invalidateRoleTemplateViews(queryClient, eventId, { catalogue: false });
        }
        throw e;
      }
      invalidateFieldQueries(existingField);
      onFieldsMutated?.();
    },
    [allFieldsState, eventId, invalidateFieldQueries, api, onFieldsMutated, queryClient, domain],
  );

  // Consumers read the allowlist-filtered `visibleFields`; internal CRUD closes
  // over the unfiltered `allFieldsState` so hidden fields are never silently
  // dropped on save.
  const contextValue = useMemo(() => ({
    allFieldsState: visibleFields, allFields: visibleFields, allFieldsUnfiltered: allFieldsState,
    createField, updateField, deleteField, setAllFieldsState,
    hasCustomApi: !!api,
  }), [visibleFields, allFieldsState, createField, updateField, deleteField, api]);

  return (
    <FieldsContext.Provider value={contextValue}>
      {children}
    </FieldsContext.Provider>
  );
};

export const useFields = () => {
  const context = useContext(FieldsContext);
  if (!context) {
    throw new Error("useFields must be used within an FieldsProvider");
  }
  return context;
};

/**
 * Non-throwing variant — returns `undefined` when rendered outside a
 * FieldsProvider. Use where a component MAY mount outside the provider (e.g. a
 * modal opened from several guest contexts) and only needs field defs as an
 * optional enhancement (preview formatting), so a missing provider degrades
 * gracefully instead of crashing.
 *
 * Callers MUST read `undefined` as "cannot tell", never as "no fields exist".
 * PB-TJ-5's dead-variable warning depends on that distinction: an absent
 * provider treated as an empty field list would mark EVERY custom-field token
 * as deleted, filling the editor with false alarms.
 */
export const useFieldsOptional = () => useContext(FieldsContext) ?? undefined;
